/* Table cell primitives and the table footer — the parts every redesigned table draws the same
 * way, in one place.
 *
 * They were three copies before this: the visits list, the supervisors list and the supervisor
 * detail's tabs each had their own status glyph, their own avatar-plus-name cell, their own
 * two-line title cell and their own rows-per-page footer. The copies drifted — the detail's team
 * table showed status as a *word* where both lists show the status ICON, its name cell was not
 * bold where the lists' are, and its row action was a bare trash button where the lists use a
 * `MoreOutlined` menu. Three tables of the same records did not look like the same product.
 *
 * The styles live in `component/list-page.scss` (`lp-*`), which is imported once from
 * `src/index.js` — see the note there about why `@use` per page is wrong.
 *
 * Everything here is presentational. Columns still belong to the page: only the page knows what
 * its records mean.
 */
import React, { useEffect, useState } from "react";
import { Avatar, Button, Dropdown, Flex, Pagination, Select, Tooltip, Typography } from "antd";
import { LeftOutlined, MoreOutlined, RightOutlined } from "@ant-design/icons";

/**
 * The table's own vertical scroll window — `scroll={{ x, y: useTableScrollY() }}`.
 *
 * The table scrolls inside itself (spec/02) so the page never scrolls, and the footer stays in
 * reach. Below the mobile breakpoint the cap goes: a 62vh window inside a phone screen is four
 * rows. Third copy of six identical lines is where it moves here.
 */
export function useTableScrollY() {
  return useIsWide() ? "62vh" : undefined;
}

/** Wider than the shell's mobile breakpoint (900px) — re-read on resize. */
export function useIsWide() {
  const test = () => window.innerWidth > 900;
  const [wide, setWide] = useState(test);
  useEffect(() => {
    const onResize = () => setWide(test());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return wide;
}

export const dash = <span className="lp-dash">—</span>;

/**
 * Coerces an API field into something React can safely render as a child — never the raw value.
 *
 * `value || dash` does NOT do this: a truthy plain object (an empty `{}` a JSONField defaults to
 * when nothing was captured, not `null`) sails straight through `||` and React throws "Objects are
 * not valid as a React child" the moment it is mounted (`/sales/orders/:id`'s `tracked_geolocation`
 * did exactly this — a Swagger-authored test record happened to hold a string there, so the bug
 * shipped invisible until a real record's empty geolocation object hit it). Guard every field read
 * straight off an API response with this rather than a bare `|| dash` / `?? dash`.
 *
 * A React element (this module's own `dash`, or a `<PersonCell>`/`<Typography.Link>` a caller
 * already built) is passed through unchanged — `typeof` alone can't tell it apart from a plain
 * object, so `React.isValidElement` is the actual test.
 */
export const textOf = value => {
  if (value === null || value === undefined || value === "") return dash;
  if (typeof value === "object" && !React.isValidElement(value)) return dash;
  return value;
};

export const fullName = user => [user?.first_name, user?.last_name].filter(Boolean).join(" ");

export const initialsOf = name =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(word => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/**
 * The leading status glyph.
 *
 * An ICON, not text: WCAG treats it as non-text content (3:1, which every `-500` clears), and the
 * glyph differs per state, so colour is never the only signal. `tone` is a second class on the
 * same element rather than a `--ok` suffix on the first, because `design-check.mjs` anchors its
 * icon exemption to a class ending in `icon` (CHECKS.md).
 *
 * @param tone one of ok | no | wait | off
 */
export function StatusIcon({ Icon, tone, label }) {
  return (
    <Tooltip title={label}>
      <Icon className={`lp-status-icon lp-status-icon--${tone}`} aria-label={label} role="img" />
    </Tooltip>
  );
}

/** The fixed 44px leading column every list table starts with. `render` supplies the glyph. */
export const statusColumn = render => ({
  key: "status",
  title: "",
  width: 44,
  align: "center",
  fixed: "left",
  render,
});

/**
 * Avatar plus name — a person, wherever one appears in a table.
 *
 * ⚠ **No `icon` prop.** The house avatar is the person's INITIALS with their photo preferred —
 * that is what the app bar's chip, the user menu, the profile page and the mockups' person cell
 * all draw. Passing `icon={<UserOutlined />}` alongside the initials silently replaced them with
 * a generic glyph on every row: antd's Avatar renders `src` → `icon` → `children`, so `icon`
 * short-circuits the children and the initials were dead code. antd already falls back to the
 * children when a photo URL fails to load, which is common on old records, so nothing is lost.
 *
 * ⚠ The font size is passed as an INLINE style, not left to `lp-who__av`. antd computes
 * `fontSize: icon ? size / 2 : 18` and writes it inline, where no stylesheet can reach it — so
 * dropping `icon` silently jumped the initials to a flat 18px inside a 28px circle, which is why
 * they read as oversized and heavy. `style` is spread last by antd, so passing it wins.
 */
export function PersonCell({ user, name, photo, strong = true }) {
  const label = name ?? fullName(user);
  if (!label) return dash;
  return (
    <Flex align="center" gap={8} className="lp-main">
      <Avatar size={28} className="lp-who__av" style={{ fontSize: 12 }} src={photo ?? user?.photo ?? null}>
        {initialsOf(label)}
      </Avatar>
      <Typography.Text strong={strong} ellipsis={{ tooltip: label }}>
        {label}
      </Typography.Text>
    </Flex>
  );
}

/** The two-line identity cell: what the row is, and where/when it is. */
export function TitleCell({ title, sub }) {
  return (
    <Flex vertical className="lp-main">
      <Typography.Text strong ellipsis={{ tooltip: title }}>
        {title || dash}
      </Typography.Text>
      {sub ? (
        <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: sub }}>
          {sub}
        </Typography.Text>
      ) : null}
    </Flex>
  );
}

/**
 * The per-row action menu.
 *
 * A menu even when it holds one item, so the affordance is in the same place and shape on every
 * table — and so a second action later does not move the first one.
 */
export function RowActions({ items, onClick, label }) {
  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation();
          onClick(key);
        },
      }}>
      <Button type="text" size="small" icon={<MoreOutlined />} aria-label={label} onClick={event => event.stopPropagation()} />
    </Dropdown>
  );
}

export const actionsColumn = render => ({
  key: "actions",
  title: "",
  width: 56,
  fixed: "right",
  render,
});

/**
 * The table footer: rows-per-page on the left, the range and the pager on the right.
 *
 * antd's `<Pagination>` fixes that order (total, pages, size changer) and its size changer has no
 * label of its own, so the two halves are laid out here and the component keeps the part it is
 * actually needed for — the page-number window.
 *
 * `labels` is `{ perPage, range }`, already translated, `range` carrying `{from} {to} {n}`.
 *
 * `sizes` is optional: omit it where the page size is not the caller's to choose — the visit's
 * reports section is one page of an Outforz proxy that forwards `page` and nothing else — and the
 * selector is left out rather than drawn as a control that cannot change anything.
 */
export function TableFoot({ id, page, size, total, sizes, onPage, onSize, labels }) {
  const from = total ? (page - 1) * size + 1 : 0;
  return (
    <Flex className="lp-table__foot" align="center" gap="small" wrap>
      {sizes ? (
        <Flex className="lp-sizesel" align="center" gap={8}>
          <Typography.Text type="secondary" id={`${id}-size`}>
            {labels.perPage}
          </Typography.Text>
          <Select size="small" value={size} aria-labelledby={`${id}-size`} options={sizes.map(n => ({ value: n, label: n }))} onChange={onSize} />
        </Flex>
      ) : null}
      <span className="lp-spacer" />
      <Typography.Text type="secondary" className="lp-num" aria-live="polite">
        {labels.range
          .split("{from}")
          .join(from)
          .split("{to}")
          .join(Math.min(page * size, total))
          .split("{n}")
          .join(total)}
      </Typography.Text>
      <Pagination current={page} pageSize={size} total={total} showSizeChanger={false} onChange={onPage} />
    </Flex>
  );
}

/**
 * The same footer, for an endpoint that reports NO total — prev/next, with no page window.
 *
 * ⚠ This is deliberately not `TableFoot` with a missing `total`. That control derives everything
 * from the total: `from` collapses to 0, the range label reads `0–0 of 0`, and antd's
 * `<Pagination total={0}>` renders a single disabled page with no next button — so paging becomes
 * impossible rather than merely unlabelled.
 *
 * Two endpoints answer with `next` and no `count`, for the same reason: a real total there is a
 * `COUNT(*)` over a filtered scan on every request. `/visit/:id`'s report section proxies
 * Outforz's `picsell_photo_report`, and `GET tasks/` now pages through
 * `NoCountResultsSetPagination` (picsell-api) because that COUNT was a second full scan of
 * `tasks_task` matching the page query row for row.
 *
 * `labels` is `{ perPage, page, prev, next }`, already translated as `TableFoot`'s are; `page`
 * carries `{n}`. `sizes` is optional for the same reason it is optional there — the visit's report
 * section forwards `page` and nothing else, so a size selector would be a control that cannot
 * change anything.
 */
export function TableSteps({ id, page, size, hasNext, sizes, onPage, onSize, labels }) {
  /* Nothing to draw on a single short page: no total means "page 1 with no next" is the only way
     to say "this is all of it", and a lone disabled pager says it worse than nothing does. */
  if (page <= 1 && !hasNext && !sizes) return null;
  return (
    <Flex className="lp-table__foot" align="center" gap="small" wrap>
      {sizes ? (
        <Flex className="lp-sizesel" align="center" gap={8}>
          <Typography.Text type="secondary" id={`${id}-size`}>
            {labels.perPage}
          </Typography.Text>
          <Select size="small" value={size} aria-labelledby={`${id}-size`} options={sizes.map(n => ({ value: n, label: n }))} onChange={onSize} />
        </Flex>
      ) : null}
      <span className="lp-spacer" />
      <Typography.Text type="secondary" className="lp-num" aria-live="polite">
        {labels.page.split("{n}").join(page)}
      </Typography.Text>
      <Button size="small" icon={<LeftOutlined />} disabled={page <= 1} onClick={() => onPage(page - 1)}>
        {labels.prev}
      </Button>
      <Button size="small" icon={<RightOutlined />} iconPosition="end" disabled={!hasNext} onClick={() => onPage(page + 1)}>
        {labels.next}
      </Button>
    </Flex>
  );
}
